const STORAGE_KEY = 'yaruta_invitation_token:v1';
const HERO_SEEN_KEY = 'yaruta_invitation_hero_seen:v1';
const INVITE_PATH = /^\/invite\/([A-Za-z0-9_-]{32,256})\/?$/;

export function invitationTokenFromPath(pathname: string): string | null {
  return pathname.match(INVITE_PATH)?.[1] ?? null;
}

export function rememberInvitationToken(token: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Storage can be unavailable in private browsing. The URL remains the
    // source of truth for the active OAuth round trip.
  }
}

/**
 * Pure read — the URL first, then the token persisted for the OAuth round
 * trip. Deliberately does NOT write: an earlier version latched whatever
 * `/invite/<...>` shape appeared in the URL into sessionStorage as a side
 * effect of being read, which meant visiting `/invite/<40 arbitrary chars>`
 * was enough to mark the whole tab as "invited" before any server call.
 * Persisting is now an explicit `rememberInvitationToken()` call made only
 * when an invite sign-in actually starts.
 */
export function getActiveInvitationToken(): string | null {
  const pathToken = invitationTokenFromPath(window.location.pathname);
  if (pathToken) return pathToken;

  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * True only while the CURRENT navigation is an invite URL, or an invite
 * sign-in is genuinely in flight (the token was stored by StepWelcome AND
 * its Hero was acknowledged). Used to exempt invitees from the
 * unsupported-domain gate — invitee roles never connect Gmail — so it must
 * not be satisfiable by simply visiting a URL of the right shape.
 */
export function hasInviteSignInInFlight(): boolean {
  if (invitationTokenFromPath(window.location.pathname) !== null) return true;

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored !== null && wasInvitationHeroSeen(stored);
  } catch {
    return false;
  }
}

export function clearInvitationToken(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(HERO_SEEN_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

export function rememberInvitationHeroSeen(token: string): void {
  try {
    window.sessionStorage.setItem(HERO_SEEN_KEY, token);
  } catch {
    // The flow can still continue when storage is unavailable.
  }
}

export function wasInvitationHeroSeen(token: string): boolean {
  try {
    return window.sessionStorage.getItem(HERO_SEEN_KEY) === token;
  } catch {
    return false;
  }
}
